import type { StarSystem } from "./types";

export interface SystemDistance {
  system: StarSystem;
  hops: number;
}

// Every system reachable from `originId` within `maxHops` (inclusive),
// via unweighted BFS over the real locked galaxy graph's `connections`
// lists -- the origin itself is included at hops=0. Pass `Infinity` for
// `maxHops` to get every reachable system in the galaxy (it's one
// connected graph, no orphans -- 16.20).
export function systemsWithinHops(
  originId: string,
  maxHops: number,
  systems: StarSystem[]
): SystemDistance[] {
  const byId = new Map(systems.map((s) => [s.id, s]));
  if (!byId.has(originId)) {
    throw new Error(`Unknown system: "${originId}"`);
  }

  const hopsById = new Map<string, number>([[originId, 0]]);
  const queue: string[] = [originId];
  let head = 0;

  while (head < queue.length) {
    const current = queue[head++];
    const currentHops = hopsById.get(current)!;
    if (currentHops >= maxHops) {
      continue;
    }
    for (const next of byId.get(current)!.connections) {
      if (!hopsById.has(next)) {
        hopsById.set(next, currentHops + 1);
        queue.push(next);
      }
    }
  }

  return [...hopsById.entries()].map(([id, hops]) => ({ system: byId.get(id)!, hops }));
}
